/**
 * TrackingErrorModal Component
 * Specialized modal for handling tracking errors with recovery options
 * Shows which legs failed and offers to track healthy legs or add to builder
 */

import type { FailedLegInfo, NewParlayLeg } from '@hooks/useTrackedParlays';
import { AlertTriangle } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface ParlayMetadata {
  tier?: string;
  confidence?: string;
  reasoning?: string;
  sportsbook?: string;
}

interface TrackingErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage: string;
  failedLegs: FailedLegInfo[];
  healthyLegs: NewParlayLeg[];
  parlayMetadata: ParlayMetadata;
  onTrackHealthyLegs: (legs: NewParlayLeg[], metadata: ParlayMetadata) => Promise<void>;
  onAddToBuilder: (legs: NewParlayLeg[]) => void;
}

// =============================================================================
// Component
// =============================================================================

export function TrackingErrorModal({
  isOpen,
  onClose,
  errorMessage: _errorMessage,
  failedLegs,
  healthyLegs,
  parlayMetadata,
  onTrackHealthyLegs,
  onAddToBuilder,
}: TrackingErrorModalProps): ReactElement | null {
  const [isTracking, setIsTracking] = useState(false);

  if (!isOpen) return null;

  const canTrackHealthy = healthyLegs.length >= 2; // Minimum for a parlay
  const canAddToBuilder = healthyLegs.length > 0;

  const handleTrackHealthy = async (): Promise<void> => {
    if (isTracking) return;
    setIsTracking(true);
    try {
      await onTrackHealthyLegs(healthyLegs, parlayMetadata);
    } finally {
      setIsTracking(false);
    }
  };

  const handleAddToBuilder = (): void => {
    onAddToBuilder(healthyLegs);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - no click to dismiss, user must use button */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-orange-500/50">
            <AlertTriangle className="w-8 h-8 text-orange-400" />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-center mb-2 text-orange-400">Cannot Track Parlay</h3>

        {/* Message */}
        <p className="text-theme-text text-center mb-4">
          The following leg{failedLegs.length > 1 ? 's are' : ' is'} missing game data and cannot be
          tracked:
        </p>

        {/* Failed legs list */}
        <div className="bg-theme-bg/50 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
          {failedLegs.map((leg, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm py-1">
              <span className="text-theme-danger">•</span>
              <span className="text-theme-text">
                {leg.player || leg.team || 'Unknown'} {leg.prop}
              </span>
            </div>
          ))}
        </div>

        {/* Recovery options explanation */}
        {(canTrackHealthy || canAddToBuilder) && (
          <p className="text-theme-text-secondary text-sm text-center mb-4">
            {canTrackHealthy
              ? `You can track the other ${healthyLegs.length} legs as a smaller parlay, or add them to the Builder to find a replacement.`
              : `You can add the ${healthyLegs.length} valid leg${healthyLegs.length > 1 ? 's' : ''} to the Builder to complete your parlay.`}
          </p>
        )}

        {/* No recovery possible */}
        {!canTrackHealthy && !canAddToBuilder && (
          <p className="text-theme-text-secondary text-sm text-center mb-4">
            All legs in this parlay have missing game data. Please try generating new picks.
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Primary actions row */}
          <div className="flex gap-3">
            {canTrackHealthy && (
              <button
                type="button"
                onClick={handleTrackHealthy}
                disabled={isTracking}
                className={`flex-1 py-2.5 rounded-lg font-medium transition ${
                  isTracking
                    ? 'bg-theme-surface-hover text-theme-text-tertiary cursor-not-allowed'
                    : 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill'
                }`}
              >
                {isTracking ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-theme-text-inverse border-t-transparent"></span>
                    Tracking...
                  </span>
                ) : (
                  `Track ${healthyLegs.length} Legs`
                )}
              </button>
            )}
            {canAddToBuilder && (
              <button
                type="button"
                onClick={handleAddToBuilder}
                className="flex-1 py-2.5 rounded-lg font-medium transition bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text"
              >
                Add to Builder
              </button>
            )}
          </div>

          {/* Cancel button */}
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-lg bg-theme-surface-hover/50 hover:bg-theme-surface-active/50 text-theme-text-secondary font-medium transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
